import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { MapPin, Trophy, Users, GraduationCap } from "lucide-react";

// The three kinds of pins shown on the interactive map.
// `size` is the marker diameter in px: simple places are deliberately smaller.
export const POINT_TYPES = {
  simple: {
    key: "simple",
    label: "Lieu où jouer",
    pluralLabel: "Lieux où jouer",
    description: "Un bar, un café ou un commerce qui possède une table.",
    Icon: MapPin,
    color: "#0ea5e9", // sky-500
    size: 26,
  },
  weekly: {
    key: "weekly",
    label: "Tournoi hebdomadaire",
    pluralLabel: "Tournois hebdomadaires",
    description: "Un lieu actif de la FQST qui organise un tournoi chaque semaine.",
    Icon: Trophy,
    color: "#f59e0b", // amber-500
    size: 38,
  },
  club: {
    key: "club",
    label: "Club de babyfoot",
    pluralLabel: "Clubs de babyfoot",
    description: "Un club organisé, avec des membres et des activités régulières.",
    Icon: Users,
    color: "#10b981", // emerald-500
    size: 38,
  },
  school: {
    key: "school",
    label: "École",
    pluralLabel: "Écoles",
    description: "Une école où l'on peut apprendre ou pratiquer le babyfoot.",
    Icon: GraduationCap,
    color: "#8b5cf6", // violet-500
    size: 38,
  },
};

// Display order everywhere (filters, list sections, form): weekly tournaments first.
export const POINT_TYPE_LIST = [
  POINT_TYPES.weekly,
  POINT_TYPES.club,
  POINT_TYPES.school,
  POINT_TYPES.simple,
];

// Types a user may create: masters can create every type, location admins only simple places.
export const getCreatableTypes = (user) => {
  if (user?.master === true) return POINT_TYPE_LIST.map((t) => t.key);
  if (user?.role === "admin") return ["simple"];
  return [];
};

// Masters manage every point; admins manage the simple places they created themselves.
export const canManagePoint = (user, point) => {
  if (!user) return false;
  if (user.master === true) return true;
  return (
    user.role === "admin" &&
    point?.type === "simple" &&
    !!point.createdBy &&
    point.createdBy === user.uid
  );
};

export const getPointType = (key) => POINT_TYPES[key] || POINT_TYPES.simple;

// A "weekly" point is linked to an active FQST location document.
export const getLinkedLocation = (point, locationsById = {}) =>
  point?.type === "weekly" && point.locationId
    ? locationsById[point.locationId] || null
    : null;

// Image precedence: the linked location's image (weekly) → the point's own image → none.
export const getPointImage = (point, locationsById) => {
  const linked = getLinkedLocation(point, locationsById);
  if (linked?.image?.trim()) return linked.image;
  return point?.image?.trim() ? point.image : null;
};

const provinceCode = (province = "") =>
  /qu[ée]bec/i.test(province) ? "QC" : /ontario/i.test(province) ? "ON" : province;

// "123 rue Principale, Montréal, QC H2X 1Y4"
export const formatFullAddress = ({ address, town, province, postalCode }) => {
  const region = [provinceCode(province), postalCode].filter((s) => s && s.trim()).join(" ");
  return [address, town, region].filter((s) => s && s.trim()).join(", ");
};

export const getPointAddress = (point) =>
  point?.fullAddress?.trim() ||
  [point?.address, point?.town].filter((s) => s && s.trim()).join(", ");

export const getDirectionsUrl = (point) =>
  `https://www.google.com/maps/dir/?api=1&destination=${point.lat},${point.lng}`;

export const sortPoints = (points) =>
  [...points].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));

// Leaflet divIcon built from the lucide icon, cached per (type, selected).
const iconCache = new Map();

export const createMarkerIcon = (typeKey, { selected = false } = {}) => {
  const cacheKey = `${typeKey}:${selected ? 1 : 0}`;
  if (iconCache.has(cacheKey)) return iconCache.get(cacheKey);

  const type = getPointType(typeKey);
  const size = type.size;
  const Icon = type.Icon;
  const html = renderToStaticMarkup(
    <div
      className={`fqst-marker${selected ? " fqst-marker--selected" : ""}`}
      style={{ width: size, height: size, background: type.color }}
      title={type.label}
    >
      <Icon size={Math.round(size * 0.55)} color="#ffffff" strokeWidth={2.5} />
    </div>
  );

  const icon = L.divIcon({
    html,
    className: "fqst-marker-wrapper",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 4],
  });
  iconCache.set(cacheKey, icon);
  return icon;
};
